import { isMobilePhoneUserAgent } from './engine';
import { isWeChatWebView } from './wechat-media-unlock';

const isIosWebKit = (userAgent: string): boolean => /iPhone|iPad|iPod/i.test(userAgent);

/**
 * H5 / 微信会把第一次 getUserMedia 的约束锁进采集会话。
 * 若第一次只开了摄像头，后面 SDK startAudioCapture 会采到静音轨。
 * 若把浏览器麦挂在预览 video 上，回声消除会把下行缓冲拉得很长。
 * /live-pro 的解法：先 getUserMedia({ audio: true }) 再整段停掉，摄像头只采画面，麦交给 SDK。
 * 桌面 Web 没有这把锁，保持这条正常链路即可。
 */
export const needsRtcMicrophoneWarmup = (
  userAgent = typeof navigator === 'undefined' ? '' : (navigator.userAgent ?? ''),
): boolean => isWeChatWebView(userAgent) || isIosWebKit(userAgent) || isMobilePhoneUserAgent(userAgent);

let hasWarmedUpRtcMicrophone = false;

export const resetRtcMicrophoneWarmupForTests = (): void => {
  hasWarmedUpRtcMicrophone = false;
};

export const warmupRtcMicrophoneIfNeeded = async (isCancelled?: () => boolean): Promise<void> => {
  if (hasWarmedUpRtcMicrophone || !needsRtcMicrophoneWarmup()) {
    return;
  }
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    return;
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => {
    if (track.readyState !== 'ended') {
      track.stop();
    }
  });
  if (isCancelled?.()) {
    return;
  }
  hasWarmedUpRtcMicrophone = true;
};

export const openRtcCameraStream = async (
  video: MediaStreamConstraints['video'],
  isCancelled?: () => boolean,
  options?: { skipWarmup?: boolean },
): Promise<MediaStream | null> => {
  if (!options?.skipWarmup && needsRtcMicrophoneWarmup() && !hasWarmedUpRtcMicrophone) {
    await warmupRtcMicrophoneIfNeeded(isCancelled);
    if (isCancelled?.()) {
      return null;
    }
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video });
  if (isCancelled?.()) {
    stream.getTracks().forEach((track) => {
      if (track.readyState !== 'ended') {
        track.stop();
      }
    });
    return null;
  }
  return stream;
};
